import React from 'react';
import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import styles from './variantButtonList.scss';
import { VariantButton } from '../variantButton/variantButton';
import { VariantType } from '../../../stores/types';
import { IInjectedProps } from '../../../types';
import TabNavigationHelper from '../../../helper/tabNavigationHelper';

export const VariantButtonList = inject('store')(
    observer(({ store, isStickyHeader, isMobileHeader }: IInjectedProps) => {
        const tabNavigation = TabNavigationHelper.instance;
        const variantStore = store?.variantStore;
        const variantSelection = store?.dataStore.variantSelection;

        const variantContainerClassNames = (isStickyHeader
            ? [styles.variants_container, styles.isStickyHeader]
            : [styles.variants_container]
        ).join(' ');

        const variantListClassNames = (isStickyHeader
            ? [styles.variants_list, styles.isStickyHeader]
            : [styles.variants_list]
        ).join(' ');

        const availableSizes = variantStore?.variantSelectionCopy.availableSizeTypes.reduce(
            (sizeTypeSum, sizeType) => sizeTypeSum + sizeType.sizes.reduce(
                (sizeVariantSum, sizeVariant) => (sizeVariant.size.code !== 0 ? sizeVariantSum + 1 : sizeVariantSum),
                0
            ),
            0
        ) ?? 0;

        // The backend can hide the switcher when exactly one size remains available.
        const showSizeSwitcher = variantSelection?.showSizeSwitcher || availableSizes === 1;
        const hasColorButton = !!variantSelection &&
            (variantSelection.showColorSwitcher || variantSelection.availableColors[0]?.code !== 0);
        const hasSizeButton = !!variantSelection && !!showSizeSwitcher;
        const hasModelButton = !!variantSelection &&
            (variantSelection.showModelSwitcher || variantSelection.availableModels[0]?.code !== 0);
        const hasVariantButtons = hasColorButton || hasSizeButton || hasModelButton;
        const variantSelectionTitle = store?.localization.generalL10n.variantSelectionTitle;

        const colorButton = () => {
            if (!hasColorButton) {
                return <></>;
            }

            return (
                <div className={classNames(styles.variants_container_item, styles.color_item)}>
                    <VariantButton
                        variantType={VariantType.Color}
                        isMobileHeader={isMobileHeader}
                        isStickyHeader={isStickyHeader}
                        isVariantCard={!isStickyHeader && !isMobileHeader}
                        isDeactive={!variantSelection?.showColorSwitcher}
                    />
                    <span className={variantListClassNames}>{variantStore?.selectedColor.name}</span>
                </div>
            );
        };

        const sizeButton = () => {
            if (!variantSelection || !showSizeSwitcher) {
                return <></>;
            }

            const isScrew = variantSelection?.isScrew;
            const sizeTableType = variantStore?.variantSelectionCopy.sizeTableType;
            const isDeactive = (variantStore?.mainStore.articleSoldOut || availableSizes < 2)
                && (!sizeTableType || sizeTableType === 'clothing');

            return isScrew ? (
                <div className={classNames(styles.variants_container_item, styles.size_item)}>
                    <VariantButton
                        variantType={VariantType.Special}
                        isMobileHeader={isMobileHeader}
                        isStickyHeader={isStickyHeader}
                        isVariantCard={!isStickyHeader && !isMobileHeader}
                    />
                    <span className={variantListClassNames} />
                </div>
                
            ) : (
                <div className={classNames(styles.variants_container_item, styles.size_item)}>
                    <VariantButton
                        variantType={VariantType.Size}
                        isMobileHeader={isMobileHeader}
                        isStickyHeader={isStickyHeader}
                        isVariantCard={!isStickyHeader && !isMobileHeader}
                        isDeactive={isDeactive}
                    />
                </div>
            )
        };

        const modelButton = () => {
            if (!hasModelButton) {
                return <></>;
            }

            return (
                <div className={classNames(styles.variants_container_item, styles.model_item)}>
                    <VariantButton
                        variantType={VariantType.Model}
                        isMobileHeader={isMobileHeader}
                        isStickyHeader={isStickyHeader}
                        isVariantCard={!isStickyHeader && !isMobileHeader}
                        isDeactive={!variantSelection?.showModelSwitcher}
                    />
                    <span className={variantListClassNames} />
                </div>
            );
        };

        return (
            hasVariantButtons &&
            <div data-testid='variant_button_list_container'
              data-semantic-id='variant_button_list_container' className={variantContainerClassNames}
                ref={(el) => tabNavigation.setGroupAtt(el)}>
                {!isStickyHeader && !isMobileHeader && variantSelectionTitle && (
                    <div className={styles.variant_card_title}>
                        {variantSelectionTitle}
                    </div>
                )}
                <div className={styles.variant_card_content}>
                    {colorButton()}
                    {sizeButton()}
                    {modelButton()}
                </div>
            </div>
        );
    })
);

export default VariantButtonList;

